/**
 * CompanySignupWizard — the public "Get Started" access request.
 *
 * Onboarding v2: this only captures enough to contact the requester (company name +
 * contact name/email/phone). NO password or user account is created here — an internal
 * admin completes the org info + banking and invites the actual users later via
 * "Setup & Activate". On submit the request is filed and the user sees a pending screen.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, User, Mail, Phone, Building2, AlertCircle, Loader2,
  CheckCircle, ClipboardCheck, Pencil, ShieldCheck, Check,
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PolicyModal from '../components/PolicyModal';
import {
  registerCompany as registerCompanyService,
  requestAccess as requestAccessService,
  getErrorMessage,
  ApiError,
  MatchedCompany,
} from '../services/authService';
import { getRegistrationSettings } from '../services/settingsService';
import {
  validateEmail, validateName, validatePhone, validateCompanyName, maskPhoneInput, formatPhone,
} from '../utils/validation';

type StepKey = 'company' | 'contact' | 'review';

interface DuplicateInfo {
  matchType: 'exact' | 'fuzzy';
  message: string;
  matchedCompany?: MatchedCompany | null;
  possibleMatches?: MatchedCompany[];
}

type AccessRequestState = 'loading' | 'sent' | 'error';

interface FormData {
  companyName: string;
  description: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

type FormErrors = Partial<Record<keyof FormData, string>>;

const EMPTY_FORM: FormData = {
  companyName: '', description: '', firstName: '', lastName: '', email: '', phone: '',
};

const STEPS: { key: StepKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'company', label: 'Company', icon: Building2 },
  { key: 'contact', label: 'Contact', icon: User },
  { key: 'review', label: 'Review', icon: ClipboardCheck },
];

const DRAFT_KEY = 'aas_signup_draft';

const inputCls =
  'w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white ' +
  'placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 ' +
  'transition-all disabled:opacity-50 disabled:cursor-not-allowed';

const CompanySignupWizard: React.FC = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyType, setPolicyType] = useState<'privacy' | 'terms'>('privacy');
  const [submitted, setSubmitted] = useState(false);

  // Duplicate-registration catch: set when register/company returns a 409 flagging the
  // signup as an exact or fuzzy match against an existing company.
  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null);
  const [accessRequestState, setAccessRequestState] = useState<Record<string, AccessRequestState>>({});

  // Company self-registration can be turned off in admin settings.
  const [registrationBlocked, setRegistrationBlocked] = useState(false);
  const [settingsChecked, setSettingsChecked] = useState(false);

  const step = STEPS[stepIndex].key;

  useEffect(() => {
    let active = true;
    getRegistrationSettings()
      .then((s) => { if (active) setRegistrationBlocked(!s.enabled || !s.companyEnabled); })
      .catch(() => { /* settings service fails open to enabled */ })
      .finally(() => { if (active) setSettingsChecked(true); });
    return () => { active = false; };
  }, []);

  // Draft save / resume (no secrets are collected, so the whole form is safe to persist).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) setFormData({ ...EMPTY_FORM, ...JSON.parse(saved) });
    } catch { /* ignore corrupt draft */ }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(formData)); } catch { /* ignore quota */ }
  }, [formData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const newValue = name === 'phone' ? maskPhoneInput(value) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (fieldErrors[name as keyof FormErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateStep = (which: StepKey): boolean => {
    const errors: FormErrors = {};
    const add = (key: keyof FormData, res: { isValid: boolean; error?: string }) => {
      if (!res.isValid) errors[key] = res.error;
    };

    if (which === 'company') {
      add('companyName', validateCompanyName(formData.companyName));
    } else if (which === 'contact') {
      add('firstName', validateName(formData.firstName, 'First name'));
      add('lastName', validateName(formData.lastName, 'Last name'));
      add('email', validateEmail(formData.email));
      add('phone', validatePhone(formData.phone));
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const goNext = () => {
    setError('');
    if (!validateStep(step)) return;
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setError('');
    setFieldErrors({});
    setStepIndex((i) => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const jumpTo = (target: StepKey) => {
    setError('');
    setFieldErrors({});
    setStepIndex(STEPS.findIndex((s) => s.key === target));
  };

  const handleSubmit = async (confirmedNotDuplicate = false) => {
    setError('');
    for (const s of ['company', 'contact'] as StepKey[]) {
      if (!validateStep(s)) { jumpTo(s); return; }
    }
    if (!termsAccepted) { setError('Please accept the Terms of Service and Privacy Policy to continue.'); return; }

    setIsLoading(true);
    try {
      await registerCompanyService({
        companyName: formData.companyName,
        description: formData.description || undefined,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        confirmedNotDuplicate,
      });
      localStorage.removeItem(DRAFT_KEY);
      setDuplicate(null);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.duplicate && apiErr.matchType) {
        setDuplicate({
          matchType: apiErr.matchType,
          message: apiErr.message,
          matchedCompany: apiErr.matchedCompany,
          possibleMatches: apiErr.possibleMatches,
        });
        setAccessRequestState({});
      } else {
        setError(getErrorMessage(err));
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  // Sends a "Request Access" notification for a given match (the exact match, or one of the
  // fuzzy candidates). Does not create any Client/User — see NotifyAccessRequestAsync backend.
  const handleRequestAccess = async (key: string, matchedClientId?: string, companyNameOverride?: string) => {
    setAccessRequestState((prev) => ({ ...prev, [key]: 'loading' }));
    try {
      await requestAccessService({
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        companyName: companyNameOverride || formData.companyName,
        matchedClientId,
      });
      setAccessRequestState((prev) => ({ ...prev, [key]: 'sent' }));
    } catch {
      setAccessRequestState((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  // "None of these — create my company": re-submits with confirmedNotDuplicate so a fuzzy
  // match no longer blocks creation (an exact match would still block on resubmit).
  const handleNotDuplicate = () => handleSubmit(true);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && step !== 'review') {
      e.preventDefault();
      goNext();
    }
  };

  const field = ({ name, label, placeholder, type = 'text', icon: Icon }: {
    name: keyof FormData; label: string; placeholder?: string; type?: string;
    icon?: React.ComponentType<{ className?: string }>;
  }) => (
    <div>
      <label className="block text-sm font-semibold text-slate-300 mb-2">
        {Icon && <Icon className="w-4 h-4 inline mr-2" />}{label}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        disabled={isLoading}
        className={inputCls}
        style={{ borderColor: fieldErrors[name] ? 'rgba(239, 68, 68, 0.5)' : undefined }}
        placeholder={placeholder}
      />
      {fieldErrors[name] && <p className="mt-2 text-sm text-red-400">{fieldErrors[name]}</p>}
    </div>
  );

  const ReviewRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="text-white text-right font-medium">{value || <span className="text-slate-500">—</span>}</span>
    </div>
  );

  const progressPct = submitted ? 100 : (stepIndex / STEPS.length) * 100 + (1 / STEPS.length) * 50;

  // Don't render until we know whether registration is enabled (no flash of the form).
  if (!settingsChecked) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        <Navigation variant="dark" />
        <main className="flex-1 flex items-center justify-center px-4 py-12 pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-[#AD0000]" />
        </main>
        <Footer />
      </div>
    );
  }

  if (registrationBlocked) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        <Navigation variant="dark" />
        <main className="flex-1 flex items-center justify-center px-4 py-12 pt-32">
          <div className="w-full max-w-md text-center">
            <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Registration is Currently Disabled</h3>
            <p className="text-slate-400 mb-6">
              New account registration is currently disabled. Please contact support or check back later.
            </p>
            <Link to="/login" className="text-[#AD0000] hover:text-red-400 font-medium">Back to sign in</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation variant="dark" />

      <div className="relative z-10 flex items-center justify-center px-4 py-8 pt-32" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className="w-full max-w-2xl">
          {!submitted && (
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-black mb-3 text-white">Get started</h1>
              <p className="text-lg text-slate-400">Tell us how to reach you and we'll set up your company access.</p>
            </div>
          )}

          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-white/10">
            {submitted ? (
              <div className="text-center py-8 animate-fade-in-up">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShieldCheck className="w-10 h-10 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Request received</h2>
                <p className="text-slate-400 mb-6 max-w-md mx-auto">
                  Thanks! Your request for <span className="text-white font-medium">{formData.companyName}</span> is in.
                  Our team will reach out at <span className="text-white font-medium">{formData.email}</span> to complete
                  your setup and get your team invited.
                </p>
                <div className="bg-slate-800/50 rounded-xl p-4 mb-6 max-w-md mx-auto text-left space-y-2">
                  <p className="text-sm text-slate-300 font-semibold">What happens next</p>
                  <p className="text-sm text-slate-400"><Phone className="w-4 h-4 inline mr-2 text-slate-300" />1. We review your request and reach out.</p>
                  <p className="text-sm text-slate-400"><ShieldCheck className="w-4 h-4 inline mr-2 text-slate-300" />2. We complete your account setup.</p>
                  <p className="text-sm text-slate-400"><CheckCircle className="w-4 h-4 inline mr-2 text-slate-300" />3. You and your team get invite emails to set a password and sign in.</p>
                </div>
                <Link to="/login" className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 rounded-xl transition-all font-bold text-white">
                  <span>Back to Login</span><ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            ) : duplicate ? (
              <div className="animate-fade-in-up">
                {duplicate.matchType === 'exact' ? (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <AlertCircle className="w-10 h-10 text-yellow-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-4">
                      {duplicate.matchedCompany?.name
                        ? <>Your organization (<span className="text-white">{duplicate.matchedCompany.name}</span>) appears to already be registered</>
                        : 'This email appears to already be registered'}
                    </h2>
                    <p className="text-slate-400 mb-6 max-w-md mx-auto">{duplicate.message}</p>

                    {accessRequestState.exact === 'sent' ? (
                      <p className="text-green-400 font-medium">Your access request has been sent.</p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRequestAccess('exact', duplicate.matchedCompany?.id, duplicate.matchedCompany?.name)}
                        disabled={accessRequestState.exact === 'loading'}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 font-bold text-white transition-all disabled:opacity-60"
                      >
                        {accessRequestState.exact === 'loading' ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : 'Request Access'}
                      </button>
                    )}
                    {accessRequestState.exact === 'error' && (
                      <p className="text-sm text-red-400 mt-3">Something went wrong sending your request. Please try again or email us.</p>
                    )}

                    <div className="mt-6">
                      <button type="button" onClick={() => setDuplicate(null)} className="text-sm text-slate-400 hover:text-white">
                        That's not right — go back and edit
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="py-2">
                    <h3 className="text-lg font-black text-white flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-yellow-400" /><span>Is your company one of these?</span>
                    </h3>
                    <p className="text-sm text-slate-400 mb-4">
                      We found similarly named companies already registered. If one of these is you, request access instead of creating a duplicate.
                    </p>

                    <div className="space-y-3 mb-6">
                      {duplicate.possibleMatches?.map((m) => (
                        <div key={m.id} className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-center justify-between gap-4">
                          <span className="text-white font-medium">{m.name}</span>
                          {accessRequestState[m.id] === 'sent' ? (
                            <span className="text-sm text-green-400 font-medium">Sent</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRequestAccess(m.id, m.id, m.name)}
                              disabled={accessRequestState[m.id] === 'loading'}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-200 hover:text-white hover:border-white/20 transition-all disabled:opacity-60"
                            >
                              {accessRequestState[m.id] === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
                              Request Access
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <button type="button" onClick={() => setDuplicate(null)} className="text-sm text-slate-400 hover:text-white">Back</button>
                      <button
                        type="button"
                        onClick={handleNotDuplicate}
                        disabled={isLoading}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 font-bold text-white transition-all disabled:opacity-60"
                      >
                        {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : 'None of these — create my company'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Stepper */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    {STEPS.map((s, i) => {
                      const Icon = s.icon;
                      const done = i < stepIndex;
                      const active = i === stepIndex;
                      return (
                        <React.Fragment key={s.key}>
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all ${
                              done ? 'bg-green-500/20 border-green-500/40 text-green-400'
                              : active ? 'bg-gradient-to-r from-[#AD0000] to-red-700 border-transparent text-white shadow-lg shadow-red-500/30'
                              : 'bg-white/5 border-white/10 text-slate-500'}`}>
                              {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                            </div>
                            <span className={`mt-2 text-xs font-semibold ${active ? 'text-white' : done ? 'text-green-400' : 'text-slate-500'}`}>{s.label}</span>
                          </div>
                          {i < STEPS.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-2 -mt-5 transition-all ${i < stepIndex ? 'bg-green-500/40' : 'bg-white/10'}`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#AD0000] to-red-700 transition-all duration-300" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-200">{error}</p>
                  </div>
                )}

                <div className="animate-fade-in-up" key={step}>
                  {step === 'company' && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-black text-white flex items-center space-x-2"><Building2 className="w-5 h-5" /><span>Company information</span></h3>
                      {field({ name: 'companyName', label: 'Company name', placeholder: 'e.g. Acme Title Services, Inc.' })}
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">Description <span className="text-slate-500 font-normal">(optional)</span></label>
                        <textarea name="description" value={formData.description} onChange={handleChange} disabled={isLoading} rows={3}
                          className={inputCls + ' resize-none'} placeholder="Brief description of your company" />
                      </div>
                    </div>
                  )}

                  {step === 'contact' && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-black text-white flex items-center space-x-2"><User className="w-5 h-5" /><span>Contact information</span></h3>
                      <p className="text-sm text-slate-400 -mt-3">How should we reach you to finish setting up your account?</p>
                      <div className="grid grid-cols-2 gap-4">
                        {field({ name: 'firstName', label: 'First name', placeholder: 'First name', icon: User })}
                        {field({ name: 'lastName', label: 'Last name', placeholder: 'Last name', icon: User })}
                      </div>
                      {field({ name: 'email', label: 'Email address', placeholder: 'you@company.com', type: 'email', icon: Mail })}
                      {field({ name: 'phone', label: 'Phone number', placeholder: '(555) 555-0100', type: 'tel', icon: Phone })}
                    </div>
                  )}

                  {step === 'review' && (
                    <div className="space-y-5">
                      <h3 className="text-lg font-black text-white flex items-center space-x-2"><ClipboardCheck className="w-5 h-5" /><span>Review &amp; submit</span></h3>

                      {[
                        { key: 'company' as StepKey, title: 'Company', rows: [['Company name', formData.companyName], ['Description', formData.description]] as [string, string][] },
                        { key: 'contact' as StepKey, title: 'Contact', rows: [['Name', `${formData.firstName} ${formData.lastName}`.trim()], ['Email', formData.email], ['Phone', formatPhone(formData.phone)]] as [string, string][] },
                      ].map((sec) => (
                        <div key={sec.key} className="rounded-xl border border-white/10 bg-white/5 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-white">{sec.title}</span>
                            <button type="button" onClick={() => jumpTo(sec.key)} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
                              <Pencil className="w-3 h-3" /> Edit
                            </button>
                          </div>
                          {sec.rows.map(([l, v]) => <ReviewRow key={l} label={l} value={v} />)}
                        </div>
                      ))}

                      <label className="flex items-start space-x-3 pt-1">
                        <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="w-4 h-4 mt-1 rounded border-white/20 bg-white/5 text-white focus:ring-[#AD0000] focus:ring-offset-0" />
                        <span className="text-sm text-slate-400">
                          I agree to the{' '}
                          <button type="button" onClick={() => { setPolicyType('terms'); setShowPolicyModal(true); }} className="text-white hover:text-gray-300 font-medium underline">Terms of Service</button>{' '}
                          and{' '}
                          <button type="button" onClick={() => { setPolicyType('privacy'); setShowPolicyModal(true); }} className="text-white hover:text-gray-300 font-medium underline">Privacy Policy</button>.
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Nav buttons */}
                <div className="flex items-center justify-between mt-8">
                  {stepIndex > 0 ? (
                    <button type="button" onClick={goBack} disabled={isLoading}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-all disabled:opacity-50">
                      <ArrowLeft className="w-5 h-5" /> Back
                    </button>
                  ) : (
                    <Link to="/login" className="text-sm text-slate-400 hover:text-white">Already have an account? Sign in</Link>
                  )}

                  {step !== 'review' ? (
                    <button type="button" onClick={goNext} disabled={isLoading}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 font-bold text-white transition-all">
                      Next <ArrowRight className="w-5 h-5" />
                    </button>
                  ) : (
                    <button type="button" onClick={() => handleSubmit()} disabled={isLoading}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 font-bold text-white transition-all disabled:opacity-60">
                      {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : <>Submit request <ArrowRight className="w-5 h-5" /></>}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <p className="text-center text-xs text-slate-500 mt-6">
            Need help? Email <a href="mailto:contact@autoaccesssolutions.org" className="text-slate-400 hover:text-white">contact@autoaccesssolutions.org</a>
          </p>
        </div>
      </div>

      <Footer />
      <PolicyModal isOpen={showPolicyModal} onClose={() => setShowPolicyModal(false)} type={policyType} />
    </div>
  );
};

export default CompanySignupWizard;
